import { useAtomValue, useSetAtom } from 'jotai';
import callApi from 'lib/callApi';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useEffect, useState } from 'react';
// Third-party libs
import Select, { StylesConfig } from 'react-select';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';
// Internal helpers & state
import { companyIdAtom } from 'views/state';

import { membersGridDataRefreshAtom } from './state';

interface MemberOption {
  value: number;
  label: string;
  email: string;
}

interface ColleagueApi {
  users_id: number;
  full_name: string;
  confirmed_at?: string | null;
  label: string;
  value: number;
  email: string;
}

export interface DeleteMemberModalProps {
  /** Whether the delete modal is open */
  isOpen: boolean;
  /** Toggle function to open/close modal */
  toggle: () => void;
  /** Member id being deleted */
  memberId: number;
  /** Full member name (for UI) */
  memberName?: string;
}

const DeleteMemberModal: React.FC<DeleteMemberModalProps> = ({
  isOpen,
  toggle,
  memberId,
  memberName,
}) => {
  const companyId = useAtomValue(companyIdAtom);
  const refreshMembersGrid = useSetAtom(membersGridDataRefreshAtom);

  const [loading, setLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedOption, setSelectedOption] = useState<'unassign' | 'reassign'>(
    'unassign',
  );
  const [pcSelectedOption, setPcSelectedOption] = useState<
    'unassign' | 'reassign'
  >('unassign');
  const [assignee, setAssignee] = useState<MemberOption | null>(null);
  const [pcAssignee, setPcAssignee] = useState<MemberOption | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasVulnerabilities, setHasVulnerabilities] = useState(false);
  const [hasPcFindings, setHasPcFindings] = useState(false);
  const [hasPcSubscription, setHasPcSubscription] = useState(false);

  // State to hold members for reassignment
  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([]);

  // Fetch members once modal opens
  useEffect(() => {
    const fetchMembers = async () => {
      if (!isOpen || !companyId) return;
      try {
        const params = {
          search: '',
          limit: 250,
          ordering: 'name',
          company_id: companyId,
          action: 'assignee',
          nutshell: 'True',
          is_active: 'True',
        };
        const res = await callApi('profiles/colleague/new/', 'GET', params);
        const results: ColleagueApi[] = res?.data?.results || [];

        const options: MemberOption[] = results
          .filter((m) => m.users_id !== memberId)
          .map((m) => ({
            value: m.users_id,
            email: m.email,
            label:
              (m.full_name as string) ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (m as any).name ||
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (m as any).email ||
              String(m.users_id),
          }));

        setMemberOptions(options);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch members list', err);
      }
    };

    void fetchMembers();
  }, [isOpen, companyId, memberId]);

  useEffect(() => {
    if (!isOpen) {
      // reset internal state when modal is closed
      setLoading(false);
      setShowAssignModal(false);
      setSelectedOption('unassign');
      setPcSelectedOption('unassign');
      setAssignee(null);
      setPcAssignee(null);
      setHasVulnerabilities(false);
      setHasPcFindings(false);
      setHasPcSubscription(false);
    }
  }, [isOpen]);

  /**
   * Primary deletion handler. Tries to delete member directly.
   * If backend responds with 422 + assigned vulnerabilities error, show assign modal.
   */
  const handleDelete = async () => {
    if (!companyId || memberId === undefined) return;

    setLoading(true);
    const loggedInUser = localStorage.getItem('loggedInUser');

    const urlParams: Record<string, unknown> = {
      id: memberId,
      loggedInUser: loggedInUser,
    };

    try {
      const res = await callApi(
        `companies/remove-member/${companyId}/`,
        'GET',
        urlParams,
      );

      if (res.status === 200 || res.status === 201) {
        showSuccessToast(res.data?.message || 'Member deleted');
        refreshMembersGrid(Math.random().toString());
        toggle();
      }
    } catch (error: any) {
      const response = error?.data || error?.response?.data || {};
      const message: string = response.message || 'Failed to delete member';

      // Check for assignments (vulnerabilities and/or PC findings)
      if (
        message?.includes('Cannot delete member because they have') &&
        (message.includes('vulnerabilities') || message.includes('PC findings'))
      ) {
        setHasVulnerabilities(response.has_vulnerabilities || false);
        setHasPcFindings(response.has_pc_findings || false);
        setHasPcSubscription(response.has_pc_subscription || false);
        setShowAssignModal(true);
      } else {
        showFailureToast(message);
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Delete with unassign / reassign action after conflicts
   */
  const handleDeleteWithAction = async () => {
    if (!companyId || memberId === undefined) return;

    // Validate vulnerability assignment selection
    if (hasVulnerabilities && selectedOption === 'reassign' && !assignee) {
      showFailureToast('Please select an assignee for vulnerabilities');
      return;
    }

    // Validate PC findings assignment selection
    if (
      hasPcFindings &&
      hasPcSubscription &&
      pcSelectedOption === 'reassign' &&
      !pcAssignee
    ) {
      showFailureToast('Please select an assignee for PC findings');
      return;
    }

    setIsDeleting(true);
    const loggedInUser = localStorage.getItem('loggedInUser');
    const urlParams: Record<string, unknown> = {
      id: memberId,
      loggedInUser,
    };

    // Handle vulnerability assignments
    if (hasVulnerabilities) {
      if (selectedOption === 'unassign') {
        urlParams['assignee'] = 'None';
      } else {
        urlParams['assignee'] = assignee?.value;
      }
    }

    // Handle PC findings assignments
    if (hasPcFindings) {
      if (!hasPcSubscription || pcSelectedOption === 'unassign') {
        // Always unassign if no subscription or user chose unassign
        urlParams['pc_assignee'] = 'None';
      } else {
        urlParams['pc_assignee'] = pcAssignee?.value;
      }
    }

    try {
      const res = await callApi(
        `companies/remove-member/${companyId}/`,
        'GET',
        urlParams,
      );

      if (res.status === 200 || res.status === 201) {
        showSuccessToast(res.data?.message || 'Member deleted');
        refreshMembersGrid(Math.random().toString());
        toggle();
      }
    } catch (error: any) {
      const response = error?.data || error?.response?.data || {};
      showFailureToast(response.message || 'Failed to delete member');
    } finally {
      setIsDeleting(false);
    }
  };

  // Prevent closing while deletion is ongoing
  const handleToggle = () => {
    if (!loading && !isDeleting) toggle();
  };

  // react-select menu portal styles so the dropdown isn't clipped by the modal overflow
  const selectStyles: StylesConfig<MemberOption, false> = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({ ...base, cursor: 'pointer' }),
    option: (base, state) => ({
      ...base,
      color: '#212529',
      backgroundColor: state.isFocused ? '#f8f9fa' : '#fff',
      cursor: 'pointer',
    }),
    singleValue: (base) => ({ ...base, color: '#212529' }),
  };

  // Render assign / unassign modal UI
  const renderAssignModal = () => {
    const getModalTitle = () => {
      const parts: string[] = [];
      if (hasVulnerabilities) parts.push('vulnerabilities');
      if (hasPcFindings) parts.push('PC findings');

      // Special case: if user has PC findings but no subscription
      if (hasPcFindings && !hasPcSubscription) {
        if (hasVulnerabilities) {
          return 'Cannot delete member with assigned vulnerabilities - PC findings will be unassigned';
        } else {
          return 'Cannot delete member - PC findings will be unassigned';
        }
      }

      return `Cannot delete members with assigned ${parts.join(' and ')}.`;
    };

    const isFormValid = () => {
      if (hasVulnerabilities && selectedOption === 'reassign' && !assignee) {
        return false;
      }
      if (
        hasPcFindings &&
        hasPcSubscription &&
        pcSelectedOption === 'reassign' &&
        !pcAssignee
      ) {
        return false;
      }
      return true;
    };

    return (
      <Modal
        isOpen={showAssignModal}
        toggle={handleToggle}
        size="md"
        backdrop={isDeleting ? 'static' : true}
      >
        <ModalHeader toggle={isDeleting ? undefined : handleToggle}>
          {getModalTitle()}
        </ModalHeader>
        <ModalBody>
          <p>
            Please choose how to handle the assignments before deleting this
            member:
          </p>
          {/* Vulnerabilities Section */}
          {hasVulnerabilities && (
            <div className="mb-4">
              <h6 className="text-primary mb-3">Vulnerabilities</h6>
              <FormGroup tag="fieldset">
                <FormGroup check>
                  <Label check className="cursor-pointer">
                    <Input
                      type="radio"
                      name="vuln_delete_option"
                      value="unassign"
                      checked={selectedOption === 'unassign'}
                      onChange={() => setSelectedOption('unassign')}
                      disabled={isDeleting}
                    />
                    Unassign vulnerabilities and delete member
                  </Label>
                </FormGroup>
                <FormGroup check className="mt-2">
                  <Label check className="cursor-pointer">
                    <Input
                      type="radio"
                      name="vuln_delete_option"
                      value="reassign"
                      checked={selectedOption === 'reassign'}
                      onChange={() => setSelectedOption('reassign')}
                      disabled={isDeleting}
                    />
                    Reassign vulnerabilities and delete member
                  </Label>
                </FormGroup>
              </FormGroup>
              {selectedOption === 'reassign' && (
                <Select
                  className="custom-360-dropdown mt-2"
                  isDisabled={isDeleting}
                  options={memberOptions}
                  onChange={(opt) => setAssignee(opt as MemberOption)}
                  value={assignee}
                  placeholder="Select assignee for vulnerabilities"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  getOptionLabel={(o) => `${o.label} (${o.email})`}
                  getOptionValue={(o) => String(o.value)}
                />
              )}
            </div>
          )}

          {/* PC Findings Section */}
          {hasPcFindings && (
            <div className="mb-3">
              <h6 className="text-primary mb-3">PC Findings</h6>
              {!hasPcSubscription && (
                <div className="alert alert-warning mb-3">
                  <small>
                    <strong>Note:</strong> You don&apos;t have access to the PC
                    Compliance module. PC findings will be automatically
                    unassigned when this member is deleted.
                  </small>
                </div>
              )}
              <FormGroup tag="fieldset">
                <FormGroup check>
                  <Label check className="cursor-pointer">
                    <Input
                      type="radio"
                      name="pc_delete_option"
                      value="unassign"
                      checked={pcSelectedOption === 'unassign'}
                      onChange={() => setPcSelectedOption('unassign')}
                      disabled={isDeleting || !hasPcSubscription}
                    />
                    Unassign PC findings and delete member
                    {!hasPcSubscription && (
                      <small className="text-muted d-block">
                        (This will happen automatically)
                      </small>
                    )}
                  </Label>
                </FormGroup>
                {hasPcSubscription && (
                  <FormGroup check className="mt-2">
                    <Label check className="cursor-pointer">
                      <Input
                        type="radio"
                        name="pc_delete_option"
                        value="reassign"
                        checked={pcSelectedOption === 'reassign'}
                        onChange={() => setPcSelectedOption('reassign')}
                        disabled={isDeleting}
                      />
                      Reassign PC findings and delete member
                    </Label>
                  </FormGroup>
                )}
              </FormGroup>
              {pcSelectedOption === 'reassign' && hasPcSubscription && (
                <Select
                  className="custom-360-dropdown mt-2"
                  isDisabled={isDeleting}
                  options={memberOptions}
                  onChange={(opt) => setPcAssignee(opt as MemberOption)}
                  value={pcAssignee}
                  placeholder="Select assignee for PC findings"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  getOptionLabel={(o) => `${o.label} (${o.email})`}
                  getOptionValue={(o) => String(o.value)}
                />
              )}
            </div>
          )}
          <div
            style={{
              backgroundColor: 'rgba(231, 241, 255, 1)', // Adaptive warning background
              border: '1px solid rgba(14, 116, 188, 1)', // Subtle border
              borderRadius: '5px',
              padding: '8px',
              marginBottom: '12px',
            }}
          >
            <p
              style={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'bold',
              }}
            >
              <i className="fa fa-info-circle mr-2"></i>
              <strong>Due Dates Behaviour</strong>
            </p>
            <p>
              <ul>
                <li>
                  Due dates will be cleared upon unassignment of vulnerabilities
                  and findings.
                </li>
                <li>
                  Due dates will not be cleared upon reassignment of
                  vulnerabilities and findings.
                </li>
              </ul>
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={handleToggle}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            disabled={isDeleting || !isFormValid()}
            onClick={handleDeleteWithAction}
          >
            {isDeleting ? <Spinner size="sm" className="mr-2" /> : null}
            Delete Member
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={handleToggle}
        size="md"
        backdrop={loading ? 'static' : true}
        keyboard={!loading}
      >
        <ModalHeader toggle={loading ? undefined : handleToggle}>
          Delete Member
        </ModalHeader>
        <ModalBody>
          {memberName ? (
            <p>
              Are you sure you want to delete <strong>{memberName}</strong>?
            </p>
          ) : (
            <p>Are you sure you want to delete this member?</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleToggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="mr-2" /> Deleting…
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Secondary modal for assign/unassign */}
      {renderAssignModal()}
    </>
  );
};

export default DeleteMemberModal;
